import React, { useRef, useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View, Animated } from "react-native";
import WebPageLayout from "../../../../layouts/WebPageLayout";
import TablePageLayout from "../../../../layouts/TablePageLayout";
import { CoreTable } from "../../../../components/CoreTable";
import { DataTable } from "react-native-paper";
import { Entypo, Feather, MaterialIcons } from "@expo/vector-icons";
import { BaseFilters } from "../../../../../web/hooks/useFilters";
import JobPlansListScreenFilters from "./components/JobPlansListScreenFilters";
import useEntityListPage from "../../../../hooks/useEntityListPage";
import useHasPermission from "../../../../../../hooks/useHasPermission";
import { useLazyGetJobPlansQuery ,useLazyGetJobPlanReportQuery} from "../../../../../../store/services/api/job-plan-api";
import { JobPlan } from "../../../../../../store/data/entities/job-plan";
import { ApprovalStatus, ApprovalStatusNames, getApprovalStatus } from "../../../../../../store/data/models/approval-status-form";
import useUser from "../../../../../../hooks/useUser";
import { Button as AntButton, Tooltip } from "antd";
import { PlusOutlined,FileExcelOutlined } from "@ant-design/icons";
import {Button} from "react-native-paper";
import { useExcelExport } from "../../../../hooks/useExport";


interface JobPlanListFilters extends BaseFilters {
  assetTypeId?: string;
  subAssetTypeId?: string;
  reference?: string;
  description?: string;
  title?: string;
  status?: string[];
  createdByUserId?: string[];
  approvedByUserId?: string[];
  createdOnStartDate?: string;
  createdOnEndDate?: string;
}

export default function JobPlansListScreen() {
  const { hasPermission } = useHasPermission();
  const { user } = useUser();
  const {exportExcel,isLoading: reportLoading} = useExcelExport({
      query: useLazyGetJobPlanReportQuery
    })

  const {
    navigateToCreate,
    navigateToEdit,
    navigateToApproval,
    navigateToReadOnlyEdit,
    onSubmitFilter,
    filter,
    tableData,
    isLoading,
  } = useEntityListPage<JobPlanListFilters>({
    entityName: "Job Plan",
    lazyTableQuery: useLazyGetJobPlansQuery,
    filterDefinition: {
      sortedColumn: "",
      sortedColumnAscending: false,
      pageNumber: 1,
      pageSize: 50,
    },
  });

  // === Filter toggle logic ===
  const [filtersVisible, setFiltersVisible] = useState(true);
  const filtersWidth = useRef(new Animated.Value(filtersVisible ? 1 : 0)).current;

  const toggleFilters = () => {
    const toValue = filtersVisible ? 0 : 1;
    Animated.timing(filtersWidth, {
      toValue,
      duration: 300,
      useNativeDriver: false,
    }).start();
    setFiltersVisible(!filtersVisible);
  };

  return (
    <WebPageLayout
      header={{
        previousTab: { title: "Dashboard", routeName: "dashboard", replaceStack: true },
        tabs: [{ title: "Job Plans", routeName: "list" }],
      }}
    >
      <TablePageLayout
        filters={
          <Animated.View
            style={{
              overflow: "hidden",
              width: filtersWidth.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 300], // adjust width as needed
              }),
              height: "100%",
            }}
          >
            <JobPlansListScreenFilters filter={filter} onSubmit={onSubmitFilter} />
          </Animated.View>
        }
        actions={
          <>
            <Button
              icon={filtersVisible ? "chevron-left" : "chevron-right"}
              mode="contained-tonal"
              onPress={toggleFilters}
              style={{ marginRight: 8 }}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>

            {hasPermission("CanCreateJobPlans") && (
              <AntButton size="large" icon={<PlusOutlined />} onClick={navigateToCreate}>
                Create Job Plan
              </AntButton>
            )}
          </>
        }
      >
        <CoreTable<JobPlan>
          isLoading={isLoading}
          headers={[
            { text: "Report"},
            { text: "Reference", sortedName: "referenceNumber" },
            { text: "Description", sortedName: "title" },
            { text: "Created By", sortedName: "createdBy" },
            { text: "Approved By", sortedName: "approvedBy" },
            { text: "Status", sortedName: "isApproved" },
            { text: "Options", flex: 0.5 },
          ]}
          data={tableData}
          keyExtractor={(item) => item.id}
          renderItem={({ item, index }) => {
            const approvalStatus = getApprovalStatus(item);
            const approvalStatusName = ApprovalStatusNames[approvalStatus];
            const showApprovalButton =
              hasPermission("CanApproveJobPlans") &&
              approvalStatus === ApprovalStatus.AwaitingApproval;
            const isAuthor = item.createdBy === user.id;
            const showEditButton =
              hasPermission("CanEditApprovedJobPlans") ||
              (hasPermission("CanEditAwaitingApprovalJobPlans") &&
                approvalStatus === ApprovalStatus.AwaitingApproval) ||
              (hasPermission("CanCreateJobPlans") &&
                approvalStatus !== ApprovalStatus.Approved &&
                isAuthor);
            const showReadOnlyEditButton = approvalStatus === ApprovalStatus.Approved && (user.role === 'Helpdesk' || user.role === 'BuildingLead');

            return (
              <CoreTable.Row
                style={index % 2 === 0 ? styles.alternateRow : styles.row}
                key={index}
              >
                <CoreTable.Cell >
                  <AntButton
                      onClick={() => exportExcel({ jobPlanId: item.id })}
                      icon={<FileExcelOutlined/>}
                      shape='circle' type="text"
                    />
                </CoreTable.Cell>
                <CoreTable.Cell>{item.reference}</CoreTable.Cell>
                <CoreTable.Cell>{item.title}</CoreTable.Cell>
                <CoreTable.Cell>{item.createdBy}</CoreTable.Cell>
                <CoreTable.Cell>{item.approvedBy ?? "N/A"}</CoreTable.Cell>
                <CoreTable.Cell>
                  {approvalStatus === ApprovalStatus.Rejected ? (
                    <Tooltip title={item.rejectedReason ?? "No reason provided"}>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                        }}
                      >
                        <Text>{approvalStatusName}</Text>
                        <Feather name="alert-circle" size={16} color="#fab005" />
                      </View>
                    </Tooltip>
                  ) : (
                    approvalStatusName
                  )}
                </CoreTable.Cell>
                <CoreTable.Cell style={{ flex: 0.5 }}>
                  {showApprovalButton && (
                    <Tooltip title="View for Approval">
                      <TouchableOpacity onPress={() => navigateToApproval(item.id)}>
                        <MaterialIcons name="approval" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                  {showEditButton && (
                    <Tooltip title="Edit">
                      <TouchableOpacity onPress={() => navigateToEdit(item.id)}>
                        <Entypo name="pencil" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                  {showReadOnlyEditButton && (
                    <Tooltip title="View">
                      <TouchableOpacity onPress={() => navigateToReadOnlyEdit(item.id)}>
                        <Entypo name="folder" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                </CoreTable.Cell>
              </CoreTable.Row>
            );
          }}
          filters={filter.filters}
          setFilter={filter.setFilter}
        />
      </TablePageLayout>
    </WebPageLayout>
  );
}

const styles = StyleSheet.create({
  row: {
    backgroundColor: "rgba(255,255,255,1)",
  },
  alternateRow: {
    backgroundColor: "rgba(245,245,245,1)",
  },
});
